import IconButton from '@/global/components/Button/IconButton'
import { SimpleChip } from '@/global/components/Chip'
import CircularProgress from '@/global/components/CircularProgress'
import { hasPermission } from '@/hooks/useBackendMenu'
import { ocorrenciaTipoToPermission } from '@/service/api'
import { CategoriaEnum } from '@acme/auth'
import { StatusAprovacao } from '@acme/core'
import { FormField } from 'global/components/Form'
import Dialog from 'global/guides/Dialog'
import { useAuth } from 'hooks'
import { CheckIcon, XIcon } from 'lucide-react'
import { Fragment, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'

interface Props {
    tipoOcorrencia: Parameters<typeof ocorrenciaTipoToPermission>[0]
    onApprove?: (dados: { id: number }) => void
    onReject?: (dados: { id: number; motivo: string }) => void
    loading?: boolean
    /** Funcionário e data da ocorrência, para o título do diálogo de reprovação. */
    referencia?: string
}

/**
 * Situação da ocorrência e, para quem aprova, os botões de aprovar e reprovar.
 * Lê `statusAprovacao` e `id` do formulário do diálogo em que está.
 */
export default function StatusOcorrencia({
    tipoOcorrencia,
    onApprove,
    onReject,
    loading,
    referencia,
}: Props) {
    const {
        decoded: { categoria },
    } = useAuth()
    const { watch } = useFormContext()
    const [reprovando, setReprovando] = useState(false)

    const statusAprovacao = watch('statusAprovacao')
    const id = watch('id')
    const { PENDENTE, PENDENTE_EXCLUSAO } = StatusAprovacao

    if (
        statusAprovacao === PENDENTE_EXCLUSAO &&
        !hasPermission(['delete', ocorrenciaTipoToPermission(tipoOcorrencia)])
    ) {
        return null
    }

    return (
        <div className="mb-4 flex grow items-center justify-end gap-2">
            <SimpleChip status={statusAprovacao} />
            {[PENDENTE, PENDENTE_EXCLUSAO].includes(statusAprovacao) &&
                [
                    CategoriaEnum.Cliente,
                    CategoriaEnum.Supervisor,
                    CategoriaEnum.Avaliacao,
                ].includes(categoria) && (
                    <Fragment>
                        <IconButton
                            onClick={() => {
                                if (!onApprove)
                                    throw new Error('onApprove não informado')
                                onApprove({ id })
                            }}
                            title={
                                statusAprovacao === PENDENTE
                                    ? 'Aprovar registro'
                                    : 'Confirmar exclusão'
                            }
                            disabled={loading}
                            className="ring-success ring"
                        >
                            {loading ? (
                                <CircularProgress size="1.5rem" />
                            ) : (
                                <CheckIcon className="text-success" />
                            )}
                        </IconButton>
                        <IconButton
                            onClick={() => setReprovando(true)}
                            title={
                                statusAprovacao === PENDENTE
                                    ? 'Reprovar registro'
                                    : 'Rejeitar exclusão'
                            }
                            disabled={loading}
                            className="ring-error ring"
                        >
                            {loading ? (
                                <CircularProgress size="1.5rem" />
                            ) : (
                                <XIcon className="text-error" />
                            )}
                        </IconButton>
                        <Dialog.Root
                            open={reprovando}
                            onClose={() => setReprovando(false)}
                            onSubmit={dados => {
                                if (!onReject)
                                    throw new Error('onReject não informado')
                                onReject(dados)
                            }}
                            defaultValues={{ id }}
                            schema={
                                onReject
                                    ? z.object({
                                          motivo: z
                                              .string()
                                              .trim()
                                              .min(1, 'Obrigatório'),
                                      })
                                    : undefined
                            }
                        >
                            <Dialog.Title
                                action={
                                    referencia ? 'Reprovar registro' : undefined
                                }
                            >
                                {referencia ?? 'Reprovar registro'}
                            </Dialog.Title>
                            <Dialog.Content>
                                <FormField
                                    autoFocus
                                    name="motivo"
                                    label="Motivo"
                                    multiline
                                    required
                                />
                                <FormField name="id" hidden />
                            </Dialog.Content>
                            <Dialog.Actions>
                                <Dialog.Actions.SecondaryButton>
                                    Cancelar
                                </Dialog.Actions.SecondaryButton>
                                <Dialog.Actions.PrimaryButton color="error">
                                    Reprovar
                                </Dialog.Actions.PrimaryButton>
                            </Dialog.Actions>
                        </Dialog.Root>
                    </Fragment>
                )}
        </div>
    )
}
